import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { UserModel } from '../../models/user.model';
import { environment } from '../../../../../environments/environment';
import { AuthModel } from '../../models/auth.model';
import { SUPPRESS_HTTP_ERROR_TOAST } from 'src/app/core/http-error-toast/http-error-toast.context';

interface RefreshAccessTokenResponse {
  accessToken: string;
}

const API_USERS_URL = `${environment.apiUrl}/auth`;
const API_PROFILE_URL = `${environment.apiUrl}/users`;

interface TokenPairResponse {
  accessToken: string;
  refreshToken: string;
}

interface ApiResponse<T> {
  data?: T;
  value?: T;
  result?: T;
}

interface GetMeResponse {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  countryCode?: string;
  phone?: string;
  birthDate?: string;
  gender?: number;
  userStatus?: number;
  userType?: number;
  selectedLanguageCode?: string;
  lastLoginDate?: string | null;
  createdDate?: string | null;
  updatedDate?: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class AuthHTTPService {
  constructor(private http: HttpClient) {}

  login(email: string, password: string): Observable<AuthModel> {
    return this.http
      .post<TokenPairResponse | ApiResponse<TokenPairResponse>>(`${API_USERS_URL}/login`, {
        email,
        password,
      })
      .pipe(map((response) => this.mapTokenResponse(response)));
  }

  refreshToken(refreshToken: string): Observable<RefreshAccessTokenResponse> {
    return this.http
      .post<RefreshAccessTokenResponse | ApiResponse<RefreshAccessTokenResponse>>(`${API_USERS_URL}/refresh`, { refreshToken })
      .pipe(map((response) => this.unwrapResponse(response)));
  }

  getUserByToken(token: string): Observable<UserModel> {
    const httpHeaders = new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });
    return this.http
      .get<GetMeResponse | ApiResponse<GetMeResponse>>(`${API_PROFILE_URL}/me`, {
        headers: httpHeaders,
        context: new HttpContext().set(SUPPRESS_HTTP_ERROR_TOAST, true),
      })
      .pipe(map((response) => this.mapUserResponse(response)));
  }

  changePassword(password: string, confirmPassword: string): Observable<void> {
    return this.http.put<void>(`${API_USERS_URL}/change-password`, {
      password,
      confirmPassword,
    });
  }

  private mapTokenResponse(response: TokenPairResponse | ApiResponse<TokenPairResponse>): AuthModel {
    const tokenPair = this.unwrapResponse(response);

    return {
      accessToken: tokenPair.accessToken,
      refreshToken: tokenPair.refreshToken,
    };
  }

  private mapUserResponse(response: GetMeResponse | ApiResponse<GetMeResponse>): UserModel {
    const user = this.unwrapResponse(response);
    const displayName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();

    return {
      ...user,
      displayName: displayName || user.email,
      avatarUrl: './assets/media/avatars/blank.png',
    };
  }

  private unwrapResponse<T>(response: T | ApiResponse<T>): T {
    const apiResponse = response as ApiResponse<T>;

    return apiResponse.data ?? apiResponse.value ?? apiResponse.result ?? (response as T);
  }
}
